import some from "lodash/some";
import { useCalculateTotalHours } from "PFApp/booking/hooks/use_calculate_total_hours";
import { Row } from "PFApp/booking/types";
import { EditMode } from "PFApp/booking/types/edit_mode";
import { Id } from "PFTypes";
import { useMemo } from "react";

import { useEditMode, useSelectModeState } from "../booking_edit_mode_store";
import {
  isChildRowSelected as isChildRowSelectedFromDomain,
  isRowSelected as isRowSelectedFromDomain
} from "../domain/selection";
import { shouldShowDurationWarning } from "../domain/validation";

export const useSelectMode = () => {
  const { calculateTotalHours } = useCalculateTotalHours();

  const editMode = useEditMode();
  const selectMode = useSelectModeState();

  const { selectableBookingIds } = selectMode;

  const isSelectable = useMemo(
    () => (bookingId: Id) => selectableBookingIds?.includes(bookingId) ?? false,
    [selectableBookingIds]
  );

  const shouldBookingBeSelected = useMemo(
    () => (bookingId: Id) => some(selectMode.filteredBookings ?? [], { id: bookingId }),
    [selectMode.filteredBookings]
  );

  const shouldBookingBeWarning = useMemo(
    () => (bookingId: Id) => {
      if (editMode !== EditMode.DraftBulkOperation) {
        return false;
      }

      const booking = selectMode.filteredBookings?.find(({ id }) => id === bookingId);
      if (!booking || !shouldBookingBeSelected(bookingId)) {
        return false;
      }

      const totalHours = calculateTotalHours(booking.startDate, booking.endDate);

      return shouldShowDurationWarning(
        selectMode.valuesToUpdate.duration?.durationType ?? null,
        selectMode.valuesToUpdate.duration?.durationValue ?? null,
        totalHours
      );
    },
    [
      editMode,
      selectMode.valuesToUpdate.duration,
      selectMode.filteredBookings,
      calculateTotalHours,
      shouldBookingBeSelected
    ]
  );

  const isRowSelected = useMemo(
    () => (row: Row) => isRowSelectedFromDomain(row, selectMode.selectedRows),
    [selectMode.selectedRows]
  );

  const isChildRowSelected = useMemo(
    () => (row: Row) => isChildRowSelectedFromDomain(row, selectMode.selectedRows),
    [selectMode.selectedRows]
  );

  return {
    isSelectable,
    shouldBookingBeSelected,
    shouldBookingBeWarning,
    isRowSelected,
    isChildRowSelected
  };
};
